import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useDebounce from 'Hooks/useDebounce';
import useUserContext from 'Context/UserContext';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';
import {
  AgentThreadListFilters,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadListHeader/components/ThreadListFilters/components/AgentThreadListFilters';
import {
  MemberThreadListFilters,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadListHeader/components/ThreadListFilters/components/MemberThreadListFilters';

export const THREAD_TYPE_QUERY_FIELD = 'threadType';
export const MESSAGE_ROUTE_STATUS_QUERY_FIELD = 'routingStatusType';
export const IS_ARCHIVED_QUERY_FIELD = 'isArchived';
export const IS_STARRED_QUERY_FIELD = 'isStarred';
export const IS_UNREAD_QUERY_FIELD = 'isUnread';
export const IS_ALL_QUERY_FIELD = 'isAll';
export const SELECTABLE_FILTER_FIELD_NAMES = [IS_ALL_QUERY_FIELD, IS_STARRED_QUERY_FIELD, IS_ARCHIVED_QUERY_FIELD, IS_UNREAD_QUERY_FIELD, THREAD_TYPE_QUERY_FIELD, MESSAGE_ROUTE_STATUS_QUERY_FIELD];

const ThreadListFilters = ({
  onSubmit,
}) => {
  const { logEvent } = useEventTrackingLogEvent({ nodeName: 'Messages Action Bar Thread Filters' });
  const {
    setSearching, clearSearching, isSearching,
  } = useMessagesContext();
  const [searchValue, setSearchValue] = useState('');
  const { resource } = useMetaLayoutContext();
  const { displayInfo, displayErrorMessage } = useSnackbarContext();
  const { isAgent } = useUserContext();

  const attributes = resource.attributes();
  const attributesArray = Object.keys(attributes);

  const clearMutuallyExclusiveFilters = (attr) => {
    attributesArray.forEach((attribute) => {
      if (SELECTABLE_FILTER_FIELD_NAMES.includes(resource.meta().field(attribute).attr('name')) && attribute !== attr) {
        resource.attr(attribute, null);
      }
    });
  };

  const numberOfSelectedFilters = attributesArray.filter((attribute) => {
    if (SELECTABLE_FILTER_FIELD_NAMES.includes(resource.meta().field(attribute).attr('name'))) {
      return resource.attr(attribute);
    }
    return false;
  }).length;

  const handleIsSearchingEvents = (value, attr) => {
    displayInfo('You may not use this filter while searching is in progress ', { timeout: 3000 });
    logEvent('Toggle clicked while searching', { toggleName: attr, value });
  };

  const handleToggle = (value, attr) => {
    if (isSearching) {
      handleIsSearchingEvents(value, attr);
      return;
    }

    // Only clear mutually exclusive filters if the user is not an agent because agents can select multiple filters.
    if (!isAgent) {
      clearMutuallyExclusiveFilters(attr);
    }

    resource.attr(attr, value);
    onSubmit(value);
  };

  const debouncedOnSubmit = useDebounce((value) => {
    setSearching();
    setSearchValue(value);
  }, 250);

  useEffect(() => {
    if (!searchValue) {
      return;
    }
    onSubmit()
      .catch(e => {
        displayErrorMessage(e, 'Error while searching, please try again.');
      })
      .finally(() => {
        clearSearching();
      });
  }, [searchValue]);

  const ThreadListFiltersComponent = isAgent ? AgentThreadListFilters : MemberThreadListFilters;

  return <ThreadListFiltersComponent debouncedOnSubmit={debouncedOnSubmit} handleToggle={handleToggle} numberOfSelectedFilters={numberOfSelectedFilters} />;
};

ThreadListFilters.defaultProps = {
};

ThreadListFilters.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export { ThreadListFilters };
